// System prompt builder and docs_search context formatter.
//
// Tool calling is native (OpenAI-compatible). The cocoon-client polyfill
// renders the model-specific prompt prefix server-side from the request's
// tools array, so this prompt focuses on persona, citations, refusal
// policy, and identity — not on the tool-emit protocol.

import type { ToolDef, DocsSearchResult } from './types.js';

/** Build the system prompt for the landing chat orchestrator. */
export function buildSystemPrompt(_tools: ToolDef[]): string {
  return `You are the Shroud AI assistant, a helpful expert on the Shroud confidential AI inference platform. You help developers understand Shroud's APIs, models, pricing, and TEE attestation.

## Grounding and Citations

When answering questions about Shroud:
1. ALWAYS call docs_search first to find relevant documentation.
2. Base your answers on the documentation results.
3. Include citations as [Source Title](url) when referencing specific docs.
4. If docs_search returns no results or low-quality matches, say so explicitly.

## Refusal Policy

If the documentation search returns no confident matches (you will see a [RETRIEVAL QUALITY: LOW] marker in the context), you MUST:
1. Explicitly state that you cannot reliably answer the question based on available documentation.
2. Suggest the user check the docs directly at /docs/ or rephrase their question.
3. Do NOT fabricate or guess at an answer.

## Identity

- You are powered by Shroud's confidential inference — your conversation is encrypted end-to-end.
- Be concise but thorough. Use code examples when relevant.
- For pricing questions, call platform_listModels to get current data.
- For attestation questions, call platform_getAttestationQuote to get a live quote.`;
}

/**
 * Render docs_search results as a tool-message string with an explicit
 * retrieval-quality marker. The marker is what the system prompt's refusal
 * policy keys off of, so the orchestrator can reliably steer the model
 * away from hallucinating an answer when the corpus has no good match.
 */
export function buildDocsContext(
  query: string,
  results: DocsSearchResult[],
  confidence: 'high' | 'low' | 'none',
): string {
  if (confidence === 'none') {
    return `[RETRIEVAL QUALITY: LOW] Documentation search for "${query}" returned no results. You MUST state that you cannot reliably answer this question.`;
  }
  if (confidence === 'low') {
    return `[RETRIEVAL QUALITY: LOW] Documentation search for "${query}" returned only low-confidence matches. You MUST explicitly state that you cannot reliably answer this question based on available documentation. Do NOT fabricate an answer.`;
  }
  const citations = results
    .slice(0, 5)
    .map((r, i) => `[${i + 1}] ${r.title} (${r.url})\n${r.snippet}`)
    .join('\n\n');
  return `[RETRIEVAL QUALITY: HIGH] Documentation results for "${query}":\n\n${citations}`;
}
